import {
  type OrganisationT,
  type OrganisationPartnerWithStatusT,
  type OrganisationUpdateT,
  type OrganisationCreateT,
} from '@ruminati/types_v2/organisation'
import { type OrganisationConnectionCreateT } from '@ruminati/types_v2/organisation_connection'
import { type OrganisationMemberT } from '@ruminati/types_v2/organisation_member'
import {
  type RelationshipCreateT,
  type RelationshipT,
  type RelationshipUpdateT,
} from '@ruminati/types_v2/relationship'
import { type RelationshipAcceptResponseT } from '@ruminati/types_v2/user'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { HTTPError } from 'ky'
import { computed } from 'vue'

import { queryKeysProfile } from '@/queries/users'
import { CommonApiV2Instance } from '@/services/common'
import { queryClient } from '@/services/queryClient'
import { useOrganisationStore, useOrgMembersStore, usePartnersStore } from '@/stores/organisations'
import { useUsersStore } from '@/stores/users'

import { defaultQueryOptions } from './queryDefaults'

type AcceptRelationshipResult =
  | RelationshipAcceptResponseT
  | { alreadyAccepted: true }
  | { success: false; reason?: string; error: unknown }

// --- Partners ---

export const queryKeysPartners = {
  all: (orgId: string | undefined) => ['partners', orgId] as const,
}

export function usePartnersQuery() {
  const organisationStore = useOrganisationStore()
  const partnersStore = usePartnersStore()

  return useQuery({
    queryKey: computed(() => queryKeysPartners.all(organisationStore.currentOrgId)),
    queryFn: async ({ signal }) => {
      const partners = await CommonApiV2Instance.get(
        `organisations/${organisationStore.currentOrgId}/partners`,
        { signal },
      ).json<Array<OrganisationPartnerWithStatusT>>()
      partnersStore.setPartners(partners)
      return partners
    },
    enabled: computed(() => !!organisationStore.currentOrgId),
    ...defaultQueryOptions,
  })
}

export function useRequestOrganisationConnection() {
  const organisationStore = useOrganisationStore()

  return useMutation({
    mutationFn: async (
      body: Pick<OrganisationConnectionCreateT, 'targetOrgId' | 'recipientOrgId'>,
    ): Promise<
      { success: true } | { success: false; errorCode?: string; context?: Record<string, unknown> }
    > => {
      try {
        await CommonApiV2Instance.post(
          `organisations/${organisationStore.currentOrg?.id}/organisation-connections/request`,
          { json: body },
        ).json()
        return { success: true }
      } catch (err) {
        if (err instanceof HTTPError) {
          try {
            const parsedErr = (await err.response.json()) as {
              code?: string
              context?: Record<string, unknown>
            }
            return { success: false, errorCode: parsedErr.code, context: parsedErr.context }
          } catch {
            // fall through
          }
        }
        return { success: false }
      }
    },
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({
          queryKey: queryKeysPartners.all(organisationStore.currentOrgId),
        })
      }
    },
  })
}

export function useDeleteOrganisationConnection() {
  const partnersStore = usePartnersStore()

  return useMutation({
    mutationFn: async (orgConnectionId: string) => {
      const response = await CommonApiV2Instance.delete(
        `organisation-connections/${orgConnectionId}`,
        { headers: { 'content-type': undefined } },
      )
      return { status: response.status, orgConnectionId }
    },
    onSuccess: ({ status, orgConnectionId }) => {
      if (status === 204) {
        const partner = partnersStore.partners?.find((p) => p.relationship?.id === orgConnectionId)
        if (partner) partner.relationship = undefined
      }
    },
  })
}

// --- Org members ---

export const queryKeysOrgMembers = {
  all: (orgId: string | undefined) => ['orgMembers', orgId] as const,
}

export function useOrgMembersQuery() {
  const organisationStore = useOrganisationStore()
  const orgMembersStore = useOrgMembersStore()

  return useQuery({
    queryKey: computed(() => queryKeysOrgMembers.all(organisationStore.currentOrgId)),
    queryFn: async ({ signal }) => {
      const members = await CommonApiV2Instance.get(
        `organisations/${organisationStore.currentOrg?.id}/members`,
        { signal },
      ).json<Array<OrganisationMemberT>>()
      orgMembersStore.setOrgMembers(members)
      return members
    },
    enabled: computed(() => !!organisationStore.currentOrgId),
    ...defaultQueryOptions,
  })
}

export function useCreateOrgMember() {
  const organisationStore = useOrganisationStore()

  return useMutation({
    mutationFn: async (
      body: RelationshipCreateT,
    ): Promise<
      { success: true } | { success: false; errorCode?: string; context?: Record<string, unknown> }
    > => {
      try {
        await CommonApiV2Instance.post(
          `organisations/${organisationStore.currentOrg?.id}/relationships`,
          { json: body },
        ).json()
        return { success: true }
      } catch (err) {
        if (err instanceof HTTPError) {
          try {
            const parsedErr = (await err.response.json()) as {
              code?: string
              context?: Record<string, unknown>
            }
            return { success: false, errorCode: parsedErr.code, context: parsedErr.context }
          } catch {
            // fall through
          }
        }
        return { success: false }
      }
    },
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({
          queryKey: queryKeysOrgMembers.all(organisationStore.currentOrgId),
        })
      }
    },
  })
}

export function useResendRelationshipInvitation() {
  const organisationStore = useOrganisationStore()

  return useMutation({
    mutationFn: async (relationshipId: string): Promise<RelationshipT> => {
      return CommonApiV2Instance.post(
        `organisations/${organisationStore.currentOrg?.id}/relationships/${relationshipId}/resend-invitation`,
        { headers: { 'content-type': undefined } },
      ).json<RelationshipT>()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeysOrgMembers.all(organisationStore.currentOrgId),
      })
    },
  })
}

export function usePatchRelationship() {
  const organisationStore = useOrganisationStore()

  return useMutation({
    mutationFn: async ({
      relationshipId,
      body,
    }: {
      relationshipId: string
      body: RelationshipUpdateT
    }) => {
      await CommonApiV2Instance.patch(`relationships/${relationshipId}`, { json: body }).json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeysOrgMembers.all(organisationStore.currentOrgId),
      })
    },
  })
}

export function useDeleteRelationship() {
  const organisationStore = useOrganisationStore()

  return useMutation({
    mutationFn: async (relationshipId: string) => {
      await CommonApiV2Instance.delete(`relationships/${relationshipId}`, {
        headers: { 'content-type': undefined },
      }).json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeysOrgMembers.all(organisationStore.currentOrgId),
      })
    },
  })
}

export function useAcceptRelationship() {
  const organisationStore = useOrganisationStore()
  const usersStore = useUsersStore()

  return useMutation({
    mutationFn: async ({
      relationshipId,
      body,
    }: {
      relationshipId: string
      body: RelationshipUpdateT
    }): Promise<AcceptRelationshipResult | undefined> => {
      let response: RelationshipAcceptResponseT
      try {
        response = await CommonApiV2Instance.patch(`relationships/${relationshipId}/accept`, {
          json: { ...body, isPending: false },
        }).json<RelationshipAcceptResponseT>()
      } catch (err) {
        if (err instanceof HTTPError) {
          let parsedErr: { context?: Record<string, unknown> } | undefined
          try {
            parsedErr = (await err.response.json()) as { context?: Record<string, unknown> }
          } catch {
            // fall through
          }

          if (err.response.status === 409 && parsedErr?.context?.reason === 'already_accepted') {
            organisationStore.removePendingOrgInvitation(relationshipId)
            await Promise.all([
              queryClient.invalidateQueries({ queryKey: queryKeysProfile.all() }),
              queryClient.invalidateQueries({
                queryKey: queryKeysOrgMembers.all(organisationStore.currentOrgId),
              }),
            ])
            return { alreadyAccepted: true }
          }

          return {
            success: false,
            reason:
              typeof parsedErr?.context?.reason === 'string' ? parsedErr.context.reason : undefined,
            error: err,
          }
        }
        return { success: false, error: err }
      }

      if (response.organisation) {
        organisationStore.upsertOrganisations([response.organisation])
      }
      if (response.relationship) {
        organisationStore.removePendingOrgInvitation(response.relationship.id)
        if (usersStore.relationships) {
          const idx = usersStore.relationships.findIndex((r) => r.id === response.relationship.id)
          if (idx > -1) usersStore.relationships.splice(idx, 1, response.relationship)
          else usersStore.relationships.push(response.relationship)
        } else {
          usersStore.relationships = [response.relationship]
        }
      }

      if (response.organisation?.id === response.relationship?.resourceId) {
        await organisationStore.changeCurrentOrg(response.relationship.resourceId)
      } else {
        await queryClient.invalidateQueries({
          queryKey: queryKeysOrgMembers.all(organisationStore.currentOrgId),
        })
      }
      await queryClient.invalidateQueries({ queryKey: queryKeysProfile.all() })

      return response
    },
  })
}

// --- Main org mutations ---

export function usePatchOrganisation() {
  const organisationStore = useOrganisationStore()

  return useMutation({
    mutationFn: async (details: OrganisationUpdateT) => {
      if (!organisationStore.currentOrg) throw new Error('No current org')
      return CommonApiV2Instance.patch(`organisations/${organisationStore.currentOrg.id}`, {
        json: details,
      }).json<OrganisationT>()
    },
    onSuccess: (patchedOrganisation) => {
      const matchingOrg = organisationStore.organisations?.find(
        (o) => o.id === patchedOrganisation.id,
      )
      if (matchingOrg) {
        Object.assign(matchingOrg, patchedOrganisation)
      } else {
        organisationStore.organisations!.push(patchedOrganisation)
      }
    },
  })
}

export function useCreateOrganisationForNewUser() {
  const organisationStore = useOrganisationStore()
  const userStore = useUsersStore()

  return useMutation({
    mutationFn: async (details: OrganisationCreateT) => {
      return CommonApiV2Instance.post('organisations/create-org-for-new-user', {
        json: details,
      }).json<{
        createdOrganisation: OrganisationT
        createdUserToOrgRelationship: RelationshipT
      }>()
    },
    onSuccess: ({ createdOrganisation, createdUserToOrgRelationship }) => {
      organisationStore.upsertOrganisations([createdOrganisation])
      organisationStore.currentOrgId = createdOrganisation.id
      userStore.upsertRelationships([createdUserToOrgRelationship])
      userStore.setPrimaryOrganisationId(createdOrganisation.id)
    },
  })
}
